import { useQuery } from "@tanstack/react-query";
import { adminFetch } from "./client";

/**
 * Tenant directory (MPPI-00298).
 *
 * The server decides whether the caller is platform staff — the SPA must not
 * infer it from the signed-in email domain. A client-side guess that disagrees
 * with `resolve_scope` shows a column of blanks to a tenant admin or hides it
 * from HouseKey staff, and neither is visible without holding two accounts.
 *
 * For a tenant-scoped caller the API returns an empty `tenants` list, so this
 * hook cannot be used to enumerate the platform's customers from a tenant
 * login even with the network tab open.
 */
export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  display_name: string;
  initials: string;
  primary_color: string;
  accent_color: string;
  is_active: boolean;
  is_platform: boolean;
}

export interface TenantDirectory {
  scope: "platform" | "tenant";
  user_group_id: string | null;
  /** Feature switches for the CALLER's tenant; absent means off. */
  features?: Record<string, boolean>;
  tenants: TenantSummary[];
}

export function useTenantDirectory() {
  const query = useQuery({
    queryKey: ["admin", "tenants", "directory"],
    queryFn: () => adminFetch<TenantDirectory>("/api/v1/admin/tenants/directory"),
    // Tenants are created about as often as customers are signed. Refetching
    // this on every table mount would be pure overhead.
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const data = query.data;
  const byId = new Map<string, TenantSummary>(
    (data?.tenants ?? []).map((t) => [t.id, t]),
  );

  return {
    isPlatform: data?.scope === "platform",
    /**
     * Optional features switched on for the caller's tenant (MPPI-00642).
     * Absent means off, the same polarity the server uses — the two must
     * agree, or the menu offers something the API refuses.
     */
    hasFeature: (key: string) => Boolean(data?.features?.[key]),
    featuresLoading: query.isLoading,
    tenants: data?.tenants ?? [],
    /** Undefined for an unknown or absent group — the badge renders "—". */
    tenantFor: (id: string | null | undefined) =>
      id ? byId.get(id) : undefined,
    /**
     * Whether a tenant column is worth the horizontal space. A platform
     * operator with exactly one tenant configured gains nothing from a column
     * whose every cell reads the same, so it stays hidden until there are two.
     */
    showTenantColumn: data?.scope === "platform" && (data?.tenants.length ?? 0) > 1,
    isLoading: query.isLoading,
  };
}
